/** @format */

import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom"; // Added useSearchParams
import {
	Calendar,
	Clock,
	ArrowRight,
	Search,
	Tag,
	BookOpen,
	Info,
} from "lucide-react"; // Refined icons

// Data (Assuming path is correct and posts have `tags: ['Inspiration', 'How-To']` etc.)
import posts from "../../blog-posts";
// Removed MailChimpSignup import - use footer version

// --- Reusable Blog Card Component ---
const BlogCard = ({ post, isFeatured = false, delay = 0 }) => {
	const {
		title,
		text,
		date,
		image,
		id,
		tags = [],
		readTime = "5 min read",
	} = post;

	const formattedDate = useMemo(
		() =>
			new Date(date).toLocaleDateString("en-US", {
				year: "numeric",
				month: "long",
				day: "numeric",
			}),
		[date]
	);

	// Different layout for featured card
	if (isFeatured) {
		return (
			<motion.article
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: delay }}
				className="relative bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden group col-span-1 md:col-span-2 lg:col-span-3" // Span full width
			>
				<Link
					to={`/blog/${id}`}
					className="block md:grid md:grid-cols-2 gap-0 items-stretch"
				>
					{/* Image (Takes full height on left) */}
					<div className="relative h-64 md:h-full overflow-hidden">
						<img
							src={image}
							alt={title}
							className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500 ease-in-out"
							loading="lazy"
						/>
						<div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>{" "}
						{/* Subtle overlay */}
						<span className="absolute top-4 left-4 px-3 py-1 bg-primary text-white rounded-full text-xs font-semibold uppercase tracking-wide">
							Featured
						</span>
					</div>
					{/* Content */}
					<div className="p-6 md:p-8 flex flex-col justify-center">
						{/* Meta */}
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
							<div className="flex items-center gap-1">
								<Calendar size={14} />{" "}
								<time dateTime={date}>{formattedDate}</time>{" "}
							</div>
							<div className="flex items-center gap-1">
								<Clock size={14} /> <span>{readTime}</span>
							</div>
							{tags.length > 0 && (
								<div className="flex items-center gap-1">
									<Tag size={14} /> <span>{tags.join(", ")}</span>
								</div>
							)}
						</div>
						{/* Title */}
						<h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors duration-200 leading-tight">
							{title}
						</h2>
						{/* Excerpt */}
						<p className="text-gray-600 text-sm md:text-base mb-5 line-clamp-3 md:line-clamp-4">
							{text}
						</p>{" "}
						{/* Limit lines */}
						{/* Read More */}
						<div className="mt-auto inline-flex items-center text-primary font-semibold group-hover:gap-3 gap-2 transition-all duration-200">
							Read Article{" "}
							<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
						</div>
					</div>
				</Link>
			</motion.article>
		);
	}

	// Regular Blog Card
	return (
		<motion.article
			initial={{ opacity: 0, y: 20 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.2 }}
			transition={{ duration: 0.4, delay: delay }}
			className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group hover:shadow-md hover:border-primary/20 transition-all duration-300 flex flex-col h-full" // Ensure consistent height
		>
			<Link to={`/blog/${id}`} className="block">
				{/* Image Container */}
				<div className="aspect-video overflow-hidden bg-gray-100">
					<img
						src={image}
						alt={title}
						className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500 ease-in-out"
						loading="lazy"
					/>
				</div>
			</Link>
			{/* Content */}
			<div className="p-5 flex flex-col flex-grow">
				{/* Meta Info */}
				<div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
					<div className="flex items-center gap-1">
						<Calendar size={14} /> <time dateTime={date}>{formattedDate}</time>{" "}
					</div>
					<div className="flex items-center gap-1">
						<Clock size={14} /> <span>{readTime}</span>
					</div>
				</div>
				<Link to={`/blog/${id}`} className="block">
					{/* Title */}
					<h2 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors duration-200 leading-snug">
						{title}
					</h2>
					{/* Excerpt */}
					<p className="text-sm text-gray-600 mb-4 line-clamp-3 flex-grow">
						{text}
					</p>{" "}
					{/* Limit lines */}
				</Link>
				{/* Tags */}
				{tags.length > 0 && (
					<div className="flex flex-wrap gap-1 mb-3">
						{tags.map((tag) => (
							<span
								key={tag}
								className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-medium"
							>
								{tag}
							</span>
						))}
					</div>
				)}
				{/* Read More Link */}
				<Link
					to={`/blog/${id}`}
					className="mt-auto inline-flex items-center text-primary text-sm font-medium group-hover:gap-2.5 gap-1.5 transition-all duration-200"
				>
					Read Article{" "}
					<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
				</Link>
			</div>
		</motion.article>
	);
};

// --- Main Blog Screen Component ---
export default function BlogScreen() {
	const [searchParams, setSearchParams] = useSearchParams(); // For managing search/filter in URL

	// --- State ---
	const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || ""); // Initialize search from URL
	// Add state for category filter if implementing
	// const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || "All");

	// --- Filtering Logic ---
	const filteredPosts = useMemo(() => {
		// 1. Sort posts by date (newest first)
		const sorted = [...posts].sort(
			(a, b) => new Date(b.date) - new Date(a.date)
		);

		// 2. Filter by search term
		const lowerSearch = searchTerm.toLowerCase();
		const searchFiltered = lowerSearch
			? sorted.filter(
					(post) =>
						post.title.toLowerCase().includes(lowerSearch) ||
						post.text.toLowerCase().includes(lowerSearch) ||
						(post.tags || []).some((tag) =>
							tag.toLowerCase().includes(lowerSearch)
						)
			  )
			: sorted;

		// 3. Filter by category (example, requires 'category' prop on posts)
		// const categoryFiltered = selectedCategory === "All"
		//     ? searchFiltered
		//     : searchFiltered.filter(post => post.category === selectedCategory);

		// return categoryFiltered; // Return final filtered list
		return searchFiltered;
	}, [searchTerm /*, selectedCategory */]); // Depend on search term and category

	// --- Handlers ---
	const handleSearchChange = (event) => {
		const newSearchTerm = event.target.value;
		setSearchTerm(newSearchTerm);
		// Update URL search parameter 'q'
		setSearchParams(
			(prev) => {
				newSearchTerm ? prev.set("q", newSearchTerm) : prev.delete("q");
				// Handle category param if implemented:
				// selectedCategory !== 'All' ? prev.set('category', selectedCategory) : prev.delete('category');
				return prev;
			},
			{ replace: true }
		); // Use replace to avoid history spam
	};

	// Handler for category filter changes (example)
	// const handleCategoryChange = (category) => {
	//     setSelectedCategory(category);
	//     setSearchParams(prev => {
	//         searchTerm ? prev.set('q', searchTerm) : prev.delete('q');
	//         category !== 'All' ? prev.set('category', category) : prev.delete('category');
	//         return prev;
	//     }, { replace: true });
	// };

	// Separate featured post logic
	const featuredPost =
		!searchTerm /* && selectedCategory === 'All' */ && filteredPosts.length > 0
			? filteredPosts[0]
			: null;
	const regularPosts = featuredPost ? filteredPosts.slice(1) : filteredPosts; // Exclude featured if shown

	return (
		<>
			<Helmet>
				<title>
					Blog: Window Treatment Ideas, Guides & Inspiration | Off Cut Shades
				</title>{" "}
				{/* SEO Title */}
				<meta
					name="description"
					content="Explore the Off Cut Shades blog for helpful tips on choosing fabrics, measuring guides, installation help, design inspiration, and the latest news on custom window shades and blinds."
				/>{" "}
				{/* SEO Desc */}
				<link rel="canonical" href="https://offcutshades.com/blog" />
				{/* Removed Breadcrumb schema for brevity */}
			</Helmet>

			<main className="min-h-screen bg-gray-50 pt-24 pb-16 md:pt-32 md:pb-24">
				{" "}
				{/* Added top padding */}
				<div className="container mx-auto px-4">
					{/* --- Header --- */}
					<div className="text-center max-w-2xl mx-auto mb-10 md:mb-12">
						<motion.div
							initial={{ scale: 0.8, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
							className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
						>
							<BookOpen size={36} strokeWidth={1.5} />
						</motion.div>
						<motion.h1
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.2 }}
							className="text-4xl md:text-5xl font-bold text-gray-900 mb-3"
						>
							Insights & Inspiration
						</motion.h1>
						<motion.p
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.3 }}
							className="text-lg text-gray-600"
						>
							Your resource for window treatment tips, design ideas, and product
							guides from the experts at Off Cut Shades.
						</motion.p>
					</div>

					{/* --- Search & Filters Bar --- */}
					<div className="max-w-3xl mx-auto mb-10 md:mb-12">
						<div className="relative">
							<input
								type="search" // Use search type
								placeholder="Search articles by keyword, topic, or tag..."
								value={searchTerm}
								onChange={handleSearchChange}
								className="w-full pl-11 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-colors text-sm"
								aria-label="Search blog posts"
							/>
							<Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
						</div>
						{/* Add Category Filter Buttons Here if implementing */}
						{/* <div className="mt-4 flex flex-wrap gap-2 justify-center">
                             {['All', 'Inspiration', 'How-To', 'Product Info'].map(cat => (
                                 <button key={cat} onClick={() => handleCategoryChange(cat)} className={`... ${selectedCategory === cat ? 'active' : ''}`}>{cat}</button>
                             ))}
                        </div> */}
					</div>

					{/* --- Blog Grid --- */}
					<AnimatePresence mode="wait">
						{filteredPosts.length > 0 ? (
							<motion.div
								key="posts-found"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								className="space-y-10 md:space-y-12"
							>
								{/* Featured Post (only if no search/filter active) */}
								{featuredPost && (
									<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
										{" "}
										{/* Wrap to control span */}
										<BlogCard
											post={featuredPost}
											isFeatured={true}
											delay={0.1}
										/>
									</div>
								)}

								{/* Regular Posts Grid */}
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
									{regularPosts.map((post, index) => (
										<BlogCard
											key={post.id}
											post={post}
											// Stagger delay, starting after featured post animation if present
											delay={
												featuredPost ? 0.3 + index * 0.07 : 0.1 + index * 0.07
											}
										/>
									))}
								</div>
							</motion.div>
						) : (
							// --- No Results State ---
							<motion.div
								key="no-posts"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								className="text-center py-16 text-gray-500 bg-white rounded-lg border border-gray-200 shadow-sm"
							>
								<Info size={40} className="mx-auto text-gray-400 mb-4" />
								<h2 className="text-xl font-semibold text-gray-800 mb-2">
									No Articles Found
								</h2>
								{searchTerm ? (
									<p>
										Your search for "{searchTerm}" didn't match any articles.
										Try different keywords or browse all posts.
									</p>
								) : (
									<p>Check back soon for new articles and updates.</p>
								)}
								{/* Optionally add Clear Search button */}
								{searchTerm && (
									<button
										onClick={() => {
											setSearchTerm("");
											setSearchParams({});
										}}
										className="mt-4 text-sm text-primary hover:underline"
									>
										Clear Search
									</button>
								)}
							</motion.div>
						)}
					</AnimatePresence>

					{/* Removed Newsletter Section - Assumed it's in Footer */}
				</div>
			</main>
		</>
	);
}
